import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useAuth, API_BASE } from "./AuthContext";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";


/**
 * TerminologySettings
 * -------------------
 * Lets a logged-in user:
 * - View all active terminology entries (UserTerminology rows).
 * - Upload one or more terminology files for any source/target pair.
 * - Soft-delete (disable) an existing terminology entry.
 *
 * This uses:
 *   GET    /terminology
 *   POST   /terminology/file   (file -> S3, returns { filename, s3_key })
 *   POST   /terminology        (metadata -> UserTerminology)
 *   DELETE /terminology/{id}
 *
 * Also uses /lang_codes to present language NAMES instead of raw codes.
 */
export default function TerminologySettings({ onBack }) {
  const { user, jwt } = useAuth();

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [prompt, setPrompt] = useState("");

  // Radio-pair selection (index into validPairs derived from workflowPairs)
  const [selectedPairIndex, setSelectedPairIndex] = useState(null);

  // Raw file selection
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  // Preview state (mirrors WorkflowBuilder TerminologyStep behavior)
  const [previewPairs, setPreviewPairs] = useState([]);
  const [previewNote, setPreviewNote] = useState("");
  const [previewPending, setPreviewPending] = useState(false);
  const [previewErr, setPreviewErr] = useState("");
  const [hasHeader, setHasHeader] = useState(false);
  const [confirmColumns, setConfirmColumns] = useState(false);

  // Language mapping from /lang_codes
  const [langCodeMap, setLangCodeMap] = useState({});
  const [langNameToCode, setLangNameToCode] = useState({});

  // Build language list (names only) for dropdowns
  const languageList = React.useMemo(
    () => Object.keys(langNameToCode).sort((a, b) => a.localeCompare(b)),
    [langNameToCode]
  );

  // --- Custom workflow language pairs (to constrain dropdowns) ---
  const [workflowPairs, setWorkflowPairs] = useState([]);
  const [workflowPairsLoaded, setWorkflowPairsLoaded] = useState(false);

  useEffect(() => {
    // Only relevant if the user is logged in and using a custom workflow
    if (!jwt || !user || !user.uses_custom_workflow) {
      setWorkflowPairs([]);
      setWorkflowPairsLoaded(false);
      return;
    }

    let cancelled = false;

    async function fetchWorkflowPairs() {
      try {
        const res = await axios.get(`${API_BASE}/workflow/lang_pairs`, {
          headers: { Authorization: `Bearer ${jwt}` },
        });
        if (!cancelled) {
          // Backend returns { pairs: [...] }, so read res.data.pairs
          const pairs =
            res.data && Array.isArray(res.data.pairs) ? res.data.pairs : [];
          setWorkflowPairs(pairs);
          setWorkflowPairsLoaded(true);
        }
      } catch (err) {
        console.error("Failed to load workflow language pairs:", err);
        if (!cancelled) {
          setWorkflowPairs([]);
          setWorkflowPairsLoaded(true);
        }
      }
    }

    fetchWorkflowPairs();

    return () => {
      cancelled = true;
    };
  }, [jwt, user]);

  // Helper: display a nice language name from a code or name
  function displayLang(codeOrName) {
    if (!codeOrName) return "";
    if (langCodeMap[codeOrName]) return langCodeMap[codeOrName];
    const lc = codeOrName.toLowerCase().replace(/_/g, "-");
    for (const [code, name] of Object.entries(langCodeMap)) {
      if (code.toLowerCase().replace(/_/g, "-") === lc) return name;
    }
    return codeOrName;
  }

  // Build valid display-name pairs from workflowPairs for radio selection
  const validPairs = React.useMemo(() => {
    if (!Array.isArray(workflowPairs) || workflowPairs.length === 0) return [];
    return workflowPairs
      .map((p) => ({
        source: displayLang(p.source),
        target: displayLang(p.target),
        sourceCode: p.source,
        targetCode: p.target,
      }))
      .filter((p) => p.source && p.target && p.source !== p.target);
  }, [workflowPairs, langCodeMap]);

  // Dropdown options built from validPairs (sorted by source, then target)
  const pairOptions = React.useMemo(() => {
    const opts = validPairs.map((p, idx) => ({
      ...p,
      label: `${p.source} \u2192 ${p.target}`,
      _idx: idx, // link back to validPairs index
    }));
    opts.sort((a, b) => {
      const s = a.source.localeCompare(b.source);
      return s !== 0 ? s : a.target.localeCompare(b.target);
    });
    return opts;
  }, [validPairs]);

  // Default-select if exactly one pair
  React.useEffect(() => {
    if (validPairs.length === 1) {
      setSelectedPairIndex(0);
    }
  }, [validPairs.length]);

  // (Dropdown options memo removed; this screen now uses radio language pairs)

  const loadEntries = () => {
    if (!jwt) {
      setEntries([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");
    axios
      .get(`${API_BASE}/terminology`, {
        headers: { Authorization: `Bearer ${jwt}` },
      })
      .then((res) => {
        const rows = Array.isArray(res.data) ? res.data : [];
        setEntries(rows);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to load terminology:", err);
        setError("Failed to load terminology entries.");
        setEntries([]);
        setLoading(false);
      });
  };

  // Load user terminology entries
  useEffect(() => {
    loadEntries();
    // eslint-disable-next-line
  }, [jwt]);

  // Load language mappings (names <-> codes) from backend /lang_codes
  useEffect(() => {
    async function fetchLangCodes() {
      try {
        const resp = await fetch(`${API_BASE}/lang_codes`);
        const data = await resp.json();
        setLangCodeMap(data.code_to_name || {});
        setLangNameToCode(data.name_to_code || {});
      } catch (err) {
        console.error("Failed to load language codes:", err);
      }
    }
    fetchLangCodes();
  }, []);

  const effectiveRows = hasHeader
    ? previewPairs.slice(1)
    : previewPairs;

  const handleFileChange = async (e) => {
    const f = e.target.files && e.target.files[0];

    setFile(f || null);
    setFileName(f ? f.name || "" : "");

    // Reset preview state whenever a new file is chosen
    setPreviewErr("");
    setPreviewPairs([]);
    setPreviewNote("");
    setHasHeader(false);
    setConfirmColumns(false);

    if (!f) {
      return;
    }

    const lower = (f.name || "").toLowerCase();
    const allowed = [".csv", ".xlsx", ".txt"];
    if (!allowed.some((ext) => lower.endsWith(ext))) {
      setPreviewErr("Unsupported file type. Please upload a CSV, XLSX, or TXT.");
      return;
    }

    try {
      setPreviewPending(true);
      const { pairs, note } = await buildTerminologyPreview(f);
      const cleaned = (pairs || []).filter(([a, b]) => a || b);
      setPreviewPairs(cleaned);
      setPreviewNote(note || "");
      if (!cleaned.length) {
        setPreviewErr(
          "No term pairs detected. Please check your file layout."
        );
      }
    } catch (err) {
      console.error("Failed to build terminology preview:", err);
      setPreviewErr("Could not parse the file for preview.");
    } finally {
      setPreviewPending(false);
    }
  };

  const resetForm = () => {
    setSelectedPairIndex(null);
    setPrompt("");
    setFile(null);
    setFileName("");
    setPreviewPairs([]);
    setPreviewNote("");
    setPreviewErr("");
    setPreviewPending(false);
    setHasHeader(false);
    setConfirmColumns(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!jwt) {
      alert("You must be logged in to manage terminology.");
      return;
    }

    if (selectedPairIndex === null || !validPairs[selectedPairIndex]) {
      alert("Please choose a language pair for this terminology file.");
      return;
    }
    if (!file) {
      alert("Please select a terminology file (TXT, CSV, or XLSX).");
      return;
    }
    if (effectiveRows.length > 0 && !confirmColumns) {
      alert("Please confirm the columns correctly represent source and target terms before saving.");
      return;
    }

    const picked = validPairs[selectedPairIndex];
    // Convert display names -> canonical codes before sending
    const srcCode = langNameToCode[picked.source] || picked.sourceCode || picked.source;
    const tgtCode = langNameToCode[picked.target] || picked.targetCode || picked.target;

    setSaving(true);
    setError("");

    try {
      // 1) Upload raw file -> /terminology/file
      const fd = new FormData();
      fd.append("file", file);

      const uploadRes = await axios.post(`${API_BASE}/terminology/file`, fd, {
        headers: {
          Authorization: `Bearer ${jwt}`,
          "Content-Type": "multipart/form-data",
        },
      });

      const { filename, s3_key } = uploadRes.data || {};
      if (!filename || !s3_key) {
        throw new Error("Upload did not return filename or s3_key.");
      }

      // 2) Create UserTerminology row -> /terminology
      const payload = {
        source_code: srcCode,
        target_code: tgtCode,
        filename,
        s3_key,
        prompt: prompt.trim() || null,
      };

      await axios.post(`${API_BASE}/terminology`, payload, {
        headers: { Authorization: `Bearer ${jwt}` },
      });

      resetForm();
      loadEntries();
      alert("Terminology file committed.");
    } catch (err) {
      console.error("Failed to commit terminology:", err);
      const detail =
        err?.response?.data?.detail ||
        "Failed to commit terminology file. Please try again.";
      setError(detail);
      alert(detail);
    } finally {
      setSaving(false);
    }
  };


  const handleDelete = async (entry) => {
    if (!jwt) {
      alert("You must be logged in to manage terminology.");
      return;
    }
    if (
      !window.confirm(
        `Remove terminology "${entry.filename}" for ${displayLang(
          entry.source_code
        )} → ${displayLang(entry.target_code)}?`
      )
    ) {
      return;
    }
    try {
      await axios.delete(`${API_BASE}/terminology/${entry.id}`, {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      loadEntries();
    } catch (err) {
      console.error("Failed to delete terminology:", err);
      const detail =
        err?.response?.data?.detail ||
        "Failed to disable terminology entry.";
      alert(detail);
    }
  };

  // Group entries by display source/target name for a nice display
  const grouped = entries.reduce((acc, e) => {
    const key = `${displayLang(e.source_code || "")} → ${displayLang(
      e.target_code || ""
    )}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(e);
    return acc;
  }, {});

  if (!user) {
    return (
      <div className="max-w-4xl mx-auto mt-8 px-4">
        <h1 className="text-2xl font-semibold mb-2">
          Terminology Enforcement Settings
        </h1>
        <p className="text-sm text-gray-700">
          Please sign in to manage your terminology.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto mt-8 px-4 pb-10">
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="mb-4 text-sm underline"
          style={{ color: "#00a7e1" }}
        >
          ← Back to Workflow Settings
        </button>
      )}

      <h1 className="text-2xl font-semibold mb-2">Terminology Settings</h1>
      <p className="text-sm text-gray-700 mb-4">
        Manage your custom terminology glossaries that are applied to your translations.
        You can create entries per source/target pair and upload TXT, CSV, or XLSX files.
        You may add multiple files for the same language pair; they&apos;ll all be listed
        and applied for that pair.
      </p>

      {/* Create / upload form */}
        <div className="mb-8 border border-gray-200 rounded-xl p-4 bg-white shadow-sm">
          <h2 className="text-lg font-semibold mb-3">Add Terminology</h2>
          <form onSubmit={handleCreate} className="space-y-3">
            <div className="mt-1">
              <div className="text-sm font-semibold text-gray-900 mb-2">
                Language Pair
              </div>

              {pairOptions.length === 0 ? (
                <div className="text-sm text-gray-600">
                  No custom language pairs found. Please edit your workflow.
                </div>
              ) : (
                <Autocomplete
                  options={pairOptions}
                  getOptionLabel={(opt) => opt.label}
                  value={
                    (selectedPairIndex !== null &&
                     pairOptions.find(o => o._idx === selectedPairIndex)) || null
                  }
                  onChange={(event, newValue) => {
                    if (!newValue) {
                      setSelectedPairIndex(null);
                      return;
                    }
                    setSelectedPairIndex(newValue._idx);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Select Language Pair"
                      variant="outlined"
                      placeholder={
                        params.inputProps.value === "" && selectedPairIndex === null
                          ? "Language Pair"
                          : ""
                      }
                    />
                  )}
                  sx={{
                    marginBottom: 1,
                    maxWidth: 520,
                    width: "100%",
                    "& .MuiInputBase-input": { fontSize: "0.875rem" },
                    "& .MuiInputLabel-root": { fontSize: "0.875rem" },
                    "& .MuiOutlinedInput-root": { borderRadius: "12px" },
                  }}
                />
              )}

            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Terminology File (TXT, CSV, or XLSX)
              </label>
              {/* Use the same pattern as in WorkflowBuilder: a button + hidden input + selected filename */}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="rounded-xl px-3 py-2 text-sm font-semibold text-white hover:opacity-90"
                style={{ backgroundColor: "#00a7e1" }}
              >
                {fileName ? "Change Terminology File" : "Choose Terminology File"}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".txt,.csv,.xlsx"
                onChange={handleFileChange}
                className="hidden"
              />
              {fileName && (
                <div className="mt-2 flex items-center gap-3 text-xs text-gray-700">
                  <span>
                    Selected:{" "}
                    <span className="font-semibold">{fileName}</span>
                  </span>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="text-xs text-red-600 underline"
                  >
                    Remove
                  </button>
                </div>
              )}
              <p className="mt-2 text-xs text-gray-600">
                If you&apos;re not sure how to format your file, please see the{" "}
                <a
                  href="https://zab-ai.helpscoutdocs.com/article/13-terminology-enforcement-how-to-format-your-terminology-file-txt-csv-xlsx"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                  style={{ color: "#00a7e1" }}
                >
                  terminology formatting guidelines
                </a>
                .
              </p>
            </div>

            {/* Preview status and table, mirroring WorkflowBuilder */}
            {previewPending && (
              <div
                className="mt-2 flex items-center text-sm"
                style={{ color: "#00a7e1" }}
              >
                <span
                  className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent align-[-0.125em]"
                  aria-hidden="true"
                />
                <span>Parsing file for preview…</span>
              </div>
            )}
            
            {previewErr && (
              <div
                className="text-sm text-red-700 mt-2"
                aria-live="polite"
              >
                {previewErr}
              </div>
            )}

            {effectiveRows.length > 0 && !previewPending && (
              <div className="mt-4">
                {previewNote && (
                  <div className="text-sm text-gray-600 mb-2">
                    {previewNote}
                  </div>
                )}

                <p className="mt-2 text-sm font-semibold text-gray-700">
                  <b>Preview:</b>
                </p>

                <div className="overflow-auto rounded border border-gray-200 mt-3">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-3 py-2 font-semibold">
                          Source Term
                        </th>
                        <th className="text-left px-3 py-2 font-semibold">
                          Target Term
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {effectiveRows.slice(0, 10).map(([src, tgt], i) => (
                        <tr
                          key={i}
                          className={i % 2 ? "bg-white" : "bg-gray-50"}
                        >
                          <td className="px-3 py-2">{src}</td>
                          <td className="px-3 py-2">{tgt}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Header row toggle – placed below the table, above confirmation (matches WorkflowBuilder) */}
                <label className="mt-3 flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={hasHeader}
                    onChange={(e) => setHasHeader(e.target.checked)}
                  />
                  <span>This file contains a header row.</span>
                </label>

                {/* Confirmation for staged file */}
                <label className="mt-3 flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={confirmColumns}
                    onChange={(e) =>
                      setConfirmColumns(e.target.checked)
                    }
                  />
                  <span>
                    I confirm these columns correctly represent{" "}
                    <span className="font-semibold">source</span> and{" "}
                    <span className="font-semibold">target</span> terms.
                  </span>
                </label>
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Additional Instructions
              </label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={3}
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                placeholder="Optional: add any special instructions about how to apply this terminology."
              />
            </div>

            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={saving || !file}
                className={`text-white px-4 py-2 rounded-xl text-sm font-semibold ${saving || !file ? "opacity-50 cursor-not-allowed" : "hover:opacity-90"}`}
                style={{ backgroundColor: "#00a7e1" }}
              >
                {saving ? "Saving..." : "Commit Terminology for Language Pair"}
              </button>
              {error && (
                <span className="text-xs text-red-600">{error}</span>
              )}
            </div>
          </form>
        </div>

      {/* Existing entries */}
      <div className="border border-gray-200 rounded-xl p-4 bg-white shadow-sm">
        <h2 className="text-lg font-semibold mb-3">Terminology Files</h2>

        {loading ? (
          <p className="text-sm text-gray-600">Loading terminology…</p>
        ) : entries.length === 0 ? (
          <p className="text-sm text-gray-600">
            You haven&apos;t added any terminology files yet.
          </p>
        ) : (
          <div className="space-y-4">
            {Object.entries(grouped).map(([pair, rows]) => (
              <div key={pair} className="border border-gray-100 rounded-lg p-3">
                <div className="text-sm font-semibold mb-2">{pair}</div>
                <div className="space-y-2">
                  {rows.map((e) => (
                    <div
                      key={e.id}
                      className="flex items-start justify-between gap-3 border-t border-gray-100 pt-2 first:border-t-0 first:pt-0"
                    >
                      <div className="text-xs text-gray-700">
                        <div>
                          <span className="font-semibold">File:</span>{" "}
                          {e.filename || "(no filename)"}
                        </div>
                        <div>
                          <span className="font-semibold">Status:</span>{" "}
                          {e.is_active ? "Active" : "Inactive"}
                        </div>
                        {e.prompt && (
                          <div className="mt-1">
                            <span className="font-semibold">Prompt:</span>{" "}
                            <span className="italic">
                              {e.prompt.length > 160
                                ? e.prompt.slice(0, 160) + "…"
                                : e.prompt}
                            </span>
                          </div>
                        )}
                        {e.created_at && (
                          <div>
                            <span className="font-semibold">Created:</span>{" "}
                            {String(e.created_at)}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <button
                          type="button"
                          onClick={() => handleDelete(e)}
                          className="px-3 py-1 rounded text-xs text-white"
                          style={{ backgroundColor: "#dc2626" }}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ========= Shared preview helpers (copied from WorkflowBuilder TerminologyStep) ========= */

function detectDelimiter(sample) {
  // Prefer tab, then comma, then semicolon
  if (/\t/.test(sample)) return "\t";
  if (/,[^\n]/.test(sample)) return ",";
  if (/;[^\n]/.test(sample)) return ";";
  return ","; // default
}

function parseCSVText(text, maxRows = 200) {
  const delim = detectDelimiter(text.slice(0, 10_000));
  const rows = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0)
    .slice(0, maxRows + 2) // +2 to help heuristics
    .map((l) => l.split(delim).map((c) => c.trim()));

  // If more than 2 cols, choose two with max non-empty values.
  if (rows.length && rows[0].length > 2) {
    const cols = rows[0].length;
    let bestPair = [0, 1],
      bestScore = -1;
    for (let i = 0; i < cols; i++) {
      for (let j = i + 1; j < cols; j++) {
        let score = 0;
        for (const r of rows) {
          if ((r[i] || "").length) score++;
          if ((r[j] || "").length) score++;
        }
        if (score > bestScore) {
          bestScore = score;
          bestPair = [i, j];
        }
      }
    }
    return rows.map((r) => [r[bestPair[0]] || "", r[bestPair[1]] || ""]);
  }
  return rows.map((r) => [r[0] || "", r[1] || ""]);
}

function parseTXTText(text, maxPairs = 200) {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  const pairs = [];

  // 1) Try tab-separated lines
  const anyTab = lines.some((l) => l.includes("\t"));
  if (anyTab) {
    for (const l of lines) {
      const [a, b] = l.split("\t");
      if (a && b) pairs.push([a.trim(), b.trim()]);
      if (pairs.length >= maxPairs) break;
    }
    if (pairs.length) return pairs;
  }

  // 2) Try comma-separated lines
  const anyComma = lines.some((l) => l.includes(","));
  if (anyComma) {
    for (const l of lines) {
      const parts = l.split(",").map((x) => x.trim());
      if (parts.length >= 2 && parts[0] && parts[1]) {
        pairs.push([parts[0], parts[1]]);
        if (pairs.length >= maxPairs) break;
      }
    }
    if (pairs.length) return pairs;
  }

  // 3) Try alternating lines: source on odd, target on even
  for (let i = 0; i + 1 < lines.length && pairs.length < maxPairs; i += 2) {
    if (lines[i] && lines[i + 1]) pairs.push([lines[i], lines[i + 1]]);
  }
  return pairs;
}

async function parseXLSXFile(file, maxRows = 200) {
  const XLSX = await import(/* webpackChunkName: "xlsx" */ "xlsx"); // dynamic, only when needed
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });
  const sheetName = wb.SheetNames[0];
  const ws = wb.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true });

  const trimmed = rows
    .filter((r) => Array.isArray(r))
    .map((r) => r.map((c) => String(c ?? "").trim()))
    .filter((r) => r.some((c) => c.length))
    .slice(0, maxRows + 2);

  if (!trimmed.length) return [];

  if (trimmed[0].length > 2) {
    // Pick two densest columns
    const cols = trimmed[0].length;
    let best = [0, 1],
      bestScore = -1;
    for (let i = 0; i < cols; i++) {
      for (let j = i + 1; j < cols; j++) {
        let score = 0;
        for (const r of trimmed) {
          if ((r[i] || "").length) score++;
          if ((r[j] || "").length) score++;
        }
        if (score > bestScore) {
          bestScore = score;
          best = [i, j];
        }
      }
    }
    return trimmed.map((r) => [r[best[0]] || "", r[best[1]] || ""]);
  }
  return trimmed.map((r) => [r[0] || "", r[1] || ""]);
}

async function buildTerminologyPreview(file) {
  const name = (file?.name || "").toLowerCase();
  if (!file) return { pairs: [], note: "" };

  if (name.endsWith(".csv")) {
    const text = await file.text();
    return {
      pairs: parseCSVText(text),
      note: "Detected CSV (2 columns preferred).",
    };
  }
  if (name.endsWith(".txt")) {
    const text = await file.text();
    return {
      pairs: parseTXTText(text),
      note: "Detected TXT (tab/comma-separated, or alternating lines).",
    };
  }
  if (name.endsWith(".xlsx")) {
    try {
      const pairs = await parseXLSXFile(file);
      return {
        pairs,
        note: "Detected XLSX (first sheet, first two relevant columns).",
      };
    } catch {
      return {
        pairs: [],
        note: "Could not parse XLSX without a proper table.",
      };
    }
  }
  return {
    pairs: [],
    note: "Unsupported file type for preview.",
  };
}
